import { Button, InlineAlert, Modal } from '@ai-tutor/ui';
import { Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { PlanPicker } from '@/components/features/billing/picker/PlanPicker';
import { usePlans, useProrationPreview } from '@/hooks/account/useBilling';
import {
  useConfirmPlanChange,
  type PlanChangePhase,
} from '@/hooks/account/useConfirmPlanChange';
import type { Plan } from '@/lib/api-client';
import {
  classifyChange,
  formatUsd,
  groupPlans,
  paidPlanFor,
  planDisplayName,
  type BillingInterval,
  type ChangeDirection,
} from '@/lib/billing';

import { ChangePlanSummary, formatPeriodDate } from './ChangePlanSummary';

/**
 * Lets a paying user switch plans, in the same `PlanPicker` onboarding and the
 * Upgrade modal use.
 *
 * Opens on the CURRENT plan's interval with the current plan selected and the
 * confirm disabled. It used to pre-select the first other plan, which for a
 * paying user was Free: a downgrade, one click from confirmed.
 *
 * Direction-aware, matching the backend (`classifyChange`):
 *
 * - **Upgrade** (monthly → yearly): switches now and is CHARGED now — the
 *   interval change resets the billing anchor. The summary quotes Stripe's
 *   preview of the exact change (`ChangePlanSummary`); a 3D Secure step on
 *   that charge is confirmed in the dialog (`useConfirmPlanChange`).
 * - **Downgrade** (yearly → monthly, or → Free): scheduled at period end —
 *   the current plan runs to the renewal date, then switches.
 */
export interface ChangePlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The user's current plan, so we mark it + classify the switch direction. */
  currentPlan: Plan;
  /** ISO current_period_end - used for the "keep until <date>" downgrade copy. */
  periodEnd: string;
  /** Called after a successful change so the parent can poll for the webhook. */
  onChanged?: () => void;
}

export function ChangePlanDialog({
  isOpen,
  onClose,
  ...rest
}: ChangePlanDialogProps) {
  // The body mounts only while open (Modal renders nothing when closed), so
  // every open starts again from the current plan.
  return (
    <Modal isOpen={isOpen} onClose={onClose} title='Change plan' size='lg'>
      <ChangePlanBody onClose={onClose} {...rest} />
    </Modal>
  );
}

/**
 * The toggle never picks a plan for the user. If it hides the one they
 * picked, the selection goes back to the current plan rather than staying on
 * a card that is no longer on screen.
 */
function selectionAfterToggle(
  plans: Plan[],
  interval: BillingInterval,
  selectedId: string,
  currentId: string
): string {
  const grouped = groupPlans(plans);
  const shown = [grouped.free, paidPlanFor(grouped, interval)];
  return shown.some(p => p?.id === selectedId) ? selectedId : currentId;
}

function ChangePlanBody({
  onClose,
  currentPlan,
  periodEnd,
  onChanged,
}: Omit<ChangePlanDialogProps, 'isOpen'>) {
  const plansQuery = usePlans();
  const plans = useMemo(() => plansQuery.data ?? [], [plansQuery.data]);
  // The catalog's copy of the current plan carries its full feature list,
  // which the "you'll lose" summary compares against.
  const current = plans.find(p => p.id === currentPlan.id) ?? currentPlan;

  const [interval, setInterval] = useState<BillingInterval>(
    currentPlan.interval === 'year' ? 'year' : 'month'
  );
  const [selectedId, setSelectedId] = useState(currentPlan.id);
  const target = plans.find(p => p.id === selectedId) ?? current;
  const direction: ChangeDirection = classifyChange(current, target);
  const isUpgrade = direction === 'upgrade_immediate';

  // Only an immediate upgrade has a price to preview; downgrades are
  // scheduled at period end and charge nothing today.
  const proration = useProrationPreview(isUpgrade ? target.id : null);

  const handleInterval = (next: BillingInterval) => {
    setInterval(next);
    setSelectedId(selectionAfterToggle(plans, next, selectedId, current.id));
  };

  const confirmChange = useConfirmPlanChange({
    onDone: () => {
      onChanged?.();
      onClose();
    },
  });
  const handleConfirm = () =>
    confirmChange.run(target.id, successMessage(target, isUpgrade, periodEnd));

  if (plansQuery.isLoading) {
    return (
      <div className='flex items-center justify-center p-10'>
        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
      </div>
    );
  }
  if (!plans.some(p => p.id !== currentPlan.id)) {
    return (
      <p className='p-6 text-sm text-muted-foreground'>
        No other billing options available right now.
      </p>
    );
  }

  return (
    <div className='space-y-5 p-6'>
      <PlanPicker
        plans={plans}
        interval={interval}
        onIntervalChange={handleInterval}
        selectedPlanId={selectedId}
        onSelect={p => setSelectedId(p.id)}
        currentPlanId={currentPlan.id}
        recommended={false}
        cardTestId={p => `target-plan-option-${p.tier}-${p.interval}`}
      />
      <ChangePlanSummary
        direction={direction}
        proration={proration}
        currentPlan={current}
        targetPlan={target}
        periodEnd={periodEnd}
      />
      {confirmChange.phase === 'auth_failed' && confirmChange.authError && (
        <div data-testid='plan-change-auth-error'>
          <InlineAlert tone='error' message={confirmChange.authError} />
        </div>
      )}
      <ConfirmRow
        direction={direction}
        target={target}
        phase={confirmChange.phase}
        chargeNow={
          isUpgrade && proration.data?.charged_now
            ? proration.data.amount_due_now_cents
            : null
        }
        priceBlocked={isUpgrade && (proration.isLoading || proration.isError)}
        onClose={onClose}
        onConfirm={handleConfirm}
        onRetry={confirmChange.retry}
      />
    </div>
  );
}

function successMessage(
  target: Plan,
  isUpgrade: boolean,
  periodEnd: string
): string {
  const name = planDisplayName(target);
  return isUpgrade
    ? `Switched to ${name}`
    : `Change scheduled - you'll move to ${name} on ${formatPeriodDate(
        periodEnd
      )}`;
}

function confirmLabel(
  direction: ChangeDirection,
  target: Plan,
  chargeNow: number | null
): string {
  if (direction === 'noop') return 'Choose a different plan';
  const name = planDisplayName(target);
  if (direction !== 'upgrade_immediate') return `Schedule switch to ${name}`;
  return chargeNow !== null
    ? `Pay ${formatUsd(chargeNow)} and switch`
    : `Switch to ${name}`;
}

const IN_FLIGHT: Partial<Record<PlanChangePhase, string>> = {
  switching: 'Switching…',
  authenticating: 'Confirming with your bank…',
};

function ConfirmRow({
  direction,
  target,
  phase,
  chargeNow,
  priceBlocked,
  onClose,
  onConfirm,
  onRetry,
}: {
  direction: ChangeDirection;
  target: Plan;
  phase: PlanChangePhase;
  /** Cents charged at the switch, when the preview says it is charged now. */
  chargeNow: number | null;
  /** An upgrade whose price has not loaded (or failed) cannot be confirmed. */
  priceBlocked: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onRetry: () => void;
}) {
  const busy = phase === 'switching' || phase === 'authenticating';
  const inFlight =
    direction === 'upgrade_immediate' ? IN_FLIGHT[phase] : 'Scheduling…';
  return (
    <div className='flex justify-end gap-2'>
      <Button
        variant='ghost'
        size='sm'
        className='max-sm:h-11'
        onClick={onClose}
        disabled={busy}
      >
        Keep current plan
      </Button>
      {phase === 'auth_failed' ? (
        <Button
          data-testid='retry-plan-change-auth'
          size='sm'
          className='max-sm:h-11'
          onClick={onRetry}
        >
          Try again
        </Button>
      ) : (
        <Button
          data-testid='confirm-plan-change'
          size='sm'
          className='max-sm:h-11'
          onClick={onConfirm}
          disabled={direction === 'noop' || busy || priceBlocked}
        >
          {busy ? (
            <>
              <Loader2 className='mr-1 h-4 w-4 animate-spin' />
              {inFlight}
            </>
          ) : (
            confirmLabel(direction, target, chargeNow)
          )}
        </Button>
      )}
    </div>
  );
}
